// Módulo Arquivos (01/10/2026, Walber) — tipos, chamadas ao motor (/api/arquivos) e utilidades.
// O motor está em app/routers/arquivos.py (todos os endereços exigem login).
// Regra do Handoff 2.1: nada de gerar PDF no servidor. A cópia fiel (HTML) vira PDF no navegador,
// pela janela de impressão ("Salvar como PDF").
import { API_URL } from './config';

export type PastaId =
  | 'fad07' | 'fad07_geral' | 'fad07_pe' | 'boletim_ponto' | 'fechamento_area'
  | 'fechamento_ciclo' | 'comunicacao' | 'liraa' | 'quadro_supervisor' | 'fora';

export interface PastaInfo {
  id: PastaId;
  nome: string;
  cor: string;        // mesma cor do cartão do módulo no menu (Dashboard.tsx)
  enviavel: boolean;  // documento que costuma ser enviado (mostra "Não enviado")
}

export const PASTAS: PastaInfo[] = [
  { id: 'fad07',             nome: 'FAD-07',                     cor: '#FF9500', enviavel: true },
  { id: 'fad07_geral',       nome: 'FAD-07 Geral',               cor: '#E65C00', enviavel: true },
  { id: 'fad07_pe',          nome: 'FAD-07 — Ponto Estratégico', cor: '#00838F', enviavel: true },
  { id: 'boletim_ponto',     nome: 'Boletim de Ponto',           cor: '#FF2D55', enviavel: false },
  { id: 'fechamento_area',   nome: 'Fechamento de Área',         cor: '#1B3A6B', enviavel: false },
  { id: 'fechamento_ciclo',  nome: 'Fechamento de Ciclo',        cor: '#1B3A6B', enviavel: true },
  { id: 'comunicacao',       nome: 'Comunicação',                cor: '#0D3B66', enviavel: true },
  { id: 'liraa',             nome: 'LIRAa',                      cor: '#0E7490', enviavel: true },
  { id: 'quadro_supervisor', nome: 'Quadro do Supervisor',       cor: '#7C3AED', enviavel: false },
  { id: 'fora',              nome: 'Documentos de fora',         cor: '#5A6661', enviavel: false },
];

export function infoPasta(id: string): PastaInfo {
  return PASTAS.find(p => p.id === id) ?? { id: 'fora', nome: id, cor: '#5A6661', enviavel: false };
}

export interface UltimoEnvio { destino: string; data_envio: string; }

export interface Arquivo {
  id: number;
  pasta: PastaId;
  tipo: string;
  chave: string;
  versao: number;
  titulo: string;
  ciclo_id: number | null;
  area_id: number | null;
  se: number | null;
  ano: number | null;
  mes: string | null;
  referencia: string | null;
  formato: 'html' | 'pdf' | 'imagem';
  tamanho_bytes: number;
  sha256: string;
  paginas: number | null;
  avisos: string[];
  origem: 'manual' | 'automatico' | 'fora';
  observacao: string | null;
  substituido_em: string | null;
  criado_em: string;
  criado_por: string | null;
  lixeira_em: string | null;
  lixeira_motivo: string | null;
  lixeira_por: string | null;
  area_nome: string | null;
  ciclo_nome: string | null;
  total_envios: number;
  ultimo_envio: UltimoEnvio | null;
  atual: boolean;
  na_lixeira: boolean;
}

export interface Envio {
  id: number;
  destino: string;
  data_envio: string;
  observacao: string | null;
  registrado_em: string;
  registrado_por: string | null;
}

export interface Detalhe { arquivo: Arquivo; versoes: Arquivo[]; envios: Envio[]; }

export interface Resumo {
  pastas: Record<PastaId, number>;
  total: number;
  lixeira: number;
  recentes: Arquivo[];
}

export interface FiltrosLista {
  pasta?: PastaId;
  ciclo_id?: number;
  area_id?: number;
  se?: number;
  busca?: string;
  lixeira?: boolean;
  limite?: number;
}

export interface NovoArquivo {
  arquivo: Blob;
  nomeArquivo: string;
  pasta: PastaId;
  tipo: string;
  chave: string;
  titulo: string;
  ciclo_id?: number | null;
  area_id?: number | null;
  se?: number | null;
  ano?: number | null;
  mes?: string | null;
  referencia?: string | null;
  paginas?: number | null;
  avisos?: string[];
  origem?: 'manual' | 'automatico' | 'fora';
  observacao?: string | null;
}

export class ErroApi extends Error {
  status: number;
  constructor(status: number, mensagem: string) {
    super(mensagem);
    this.status = status;
  }
}

export const LIMITE_BYTES = 20 * 1024 * 1024;

function cabecalho(token: string | null): Record<string, string> {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function erroDaResposta(r: Response): Promise<ErroApi> {
  let mensagem = `Erro ${r.status}`;
  try {
    const j = await r.json();
    if (j && j.detail) mensagem = typeof j.detail === 'string' ? j.detail : 'Dados recusados pelo sistema.';
  } catch { /* resposta sem JSON (ex.: página de erro do nginx) */ }
  if (r.status === 401) mensagem = 'Sua sessão expirou. Entre de novo no TOVIS.';
  if (r.status === 413) mensagem = 'Arquivo grande demais (máximo 20 MB).';
  return new ErroApi(r.status, mensagem);
}

async function pedir<T>(token: string | null, caminho: string, init: RequestInit = {}): Promise<T> {
  const headers = { ...cabecalho(token), ...((init.headers as Record<string, string>) || {}) };
  const r = await fetch(`${API_URL}/api/arquivos${caminho}`, { ...init, headers });
  if (!r.ok) throw await erroDaResposta(r);
  return r.json() as Promise<T>;
}

function json(corpo: unknown): RequestInit {
  return { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corpo) };
}

export const arquivosApi = {
  resumo: (token: string | null, cicloId?: number) =>
    pedir<Resumo>(token, `/resumo${cicloId ? `?ciclo_id=${cicloId}` : ''}`),

  listar: (token: string | null, f: FiltrosLista) => {
    const qs = new URLSearchParams();
    (Object.keys(f) as (keyof FiltrosLista)[]).forEach(k => {
      const v = f[k];
      if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    });
    const s = qs.toString();
    return pedir<Arquivo[]>(token, s ? `?${s}` : '');
  },

  detalhe: (token: string | null, id: number) => pedir<Detalhe>(token, `/${id}`),

  conteudo: async (token: string | null, id: number): Promise<Blob> => {
    const r = await fetch(`${API_URL}/api/arquivos/${id}/conteudo`, { headers: cabecalho(token) });
    if (!r.ok) throw await erroDaResposta(r);
    return r.blob();
  },

  arquivar: (token: string | null, n: NovoArquivo) => {
    const fd = new FormData();
    fd.append('arquivo', n.arquivo, n.nomeArquivo);
    fd.append('pasta', n.pasta);
    fd.append('tipo', n.tipo);
    fd.append('chave', n.chave);
    fd.append('titulo', n.titulo);
    const opcionais: [string, unknown][] = [
      ['ciclo_id', n.ciclo_id], ['area_id', n.area_id], ['se', n.se], ['ano', n.ano], ['mes', n.mes],
      ['referencia', n.referencia], ['paginas', n.paginas], ['observacao', n.observacao],
    ];
    opcionais.forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') fd.append(k, String(v)); });
    fd.append('avisos', JSON.stringify(n.avisos ?? []));
    fd.append('origem', n.origem ?? 'manual');
    return pedir<Arquivo & { substituiu_versao: number | null }>(token, '', { method: 'POST', body: fd });
  },

  registrarEnvio: (token: string | null, id: number, corpo: { destino: string; data_envio: string; observacao?: string }) =>
    pedir<Detalhe>(token, `/${id}/envios`, json(corpo)),

  lixeira: (token: string | null, id: number, motivo: string) =>
    pedir<Arquivo>(token, `/${id}/lixeira`, json({ motivo })),

  restaurar: (token: string | null, id: number) =>
    pedir<Arquivo>(token, `/${id}/restaurar`, { method: 'POST' }),
};

// ── Utilidades de exibição ──────────────────────────────────────────────────

export function mensagemDeErro(e: unknown): string {
  if (e instanceof ErroApi) return e.message;
  if (e instanceof Error && e.message) return e.message.includes('Failed to fetch')
    ? 'Sem conexão com o TOVIS. Confira a internet e tente de novo.' : e.message;
  return 'Algo deu errado. Tente de novo.';
}

export function formatarDataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/** 'AAAA-MM-DD' → 'DD/MM/AAAA' (sem passar por fuso horário). */
export function formatarDia(dia: string | null | undefined, comAno = true): string {
  if (!dia) return '—';
  const [a, m, d] = dia.slice(0, 10).split('-');
  if (!a || !m || !d) return dia;
  return comAno ? `${d}/${m}/${a}` : `${d}/${m}`;
}

export function hojeISO(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** Nome sugerido ao salvar/baixar (sem caracteres que o Windows recusa). */
export function nomeParaSalvar(a: Pick<Arquivo, 'titulo' | 'versao'>): string {
  return `${a.titulo} v${a.versao}`.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
}
